import { useMsal } from '@azure/msal-react';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useContext, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { TiVendorMicrosoft } from 'react-icons/ti';
import { Link, useNavigate } from 'react-router-dom'; // IMPORT Link for Forgot Password
import { AuthContext } from '../../context/AuthContext';

const LoginForm = () => {
  const navigate = useNavigate();
  // Ensure your AuthContext exposes requiresReset if you added it previously
  const { login, isAuthenticated, requiresReset, inProgress } = useContext(AuthContext); 
  const { instance } = useMsal();

  const [view, setView] = useState<'ad' | 'local'>('ad');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false); // NEW STATE
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Load remembered email on component mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  // Handle redirect when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      if (requiresReset) {
        navigate('/change-password', { state: { forcedReset: true } });
      } else {
        navigate('/');
      }
    }
  }, [isAuthenticated, requiresReset, navigate]);

  const isAdInProgress = (inProgress === "startup" || inProgress === "login");

  useEffect(() => {
    if (isAdInProgress) {
      setView("ad");
    } else {
      setView("ad");
    }
  }, [inProgress]);

  const handleADLogin = () => {
    setIsLoading(true);
    instance.loginRedirect({
      scopes: ['User.Read', 'User.Read.All'],
    }).catch(err => {
      setError(err.message);
      setIsLoading(false);
    });
  };

  const handleLocalLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      // Pass rememberMe flag to the context login function
      await login(email, password, rememberMe);
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const variants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 }
  };

  return (
    <div className="d-flex flex-column gap-3">
      {/* Toggle Buttons */}
      {/* <div className="btn-group w-100 mb-3" role="group">
        <button
          className={`btn btn-outline-primary ${view === 'local' ? 'active' : ''}`}
          onClick={() => setView('local')}
        >
          User Name/Password
        </button>
        <button
          className={`btn btn-outline-primary ${view === 'ad' ? 'active' : ''}`}
          onClick={() => setView('ad')}
          title='Under progress'
        >
          <span className='d-flex flex-row align-items-center gap-2 w-100 justify-content-center'>
            Sign in with Microsoft <TiVendorMicrosoft size={16} />
          </span>
        </button>
      </div> */}

      {/* Animated Section */}
      <AnimatePresence mode="wait">
        {view === 'local' ? (
          <motion.form
            key="local"
            onSubmit={handleLocalLogin}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={variants}
            className="d-flex flex-column gap-3"
          >
            <div>
              <label className="form-label text-muted small fw-bold mb-1">Email</label>
              <input
                aria-label='email'
                type="email"
                className="form-control form-control-lg fs-6"
                value={email}
                required
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
              />
            </div>
            
            <div>
              <label className="form-label text-muted small fw-bold mb-1">Password</label>
              <input
                aria-label='password'
                type="password"
                className="form-control form-control-lg fs-6"
                value={password}
                required
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
              />
            </div>

            {/* NEW: Remember Me & Forgot Password Row */}
            <div className="d-flex justify-content-between align-items-center mt-1 mb-2">
              <div className="form-check">
                <input 
                  className="form-check-input shadow-none cursor-pointer" 
                  type="checkbox" 
                  id="rememberMeCheck" 
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <label className="form-check-label text-muted small" htmlFor="rememberMeCheck" style={{ cursor: 'pointer', userSelect: 'none' }}>
                  Remember me
                </label>
              </div>
              {/* <Link to="/forgot-password" className="text-decoration-none small fw-semibold text-primary">
                Forgot password?
              </Link> */}
            </div>

            {error && <div className="alert alert-danger py-2 small mb-0">{error}</div>}
            
            <button className="btn btn-primary btn-lg fs-6 mt-1 shadow-sm" type="submit" disabled={isLoading}>
              {isLoading ? (
                <span><Spinner animation="border" size="sm" className="me-2" /> <span>Logging in...</span></span>
              ) : 'Login'}
            </button>
          </motion.form>
        ) : (
          <motion.div
            key="ad"
            initial="initial"
            animate="animate"
            exit="exit"
            variants={variants}
            className="text-center"
          >
            {
              (isAdInProgress) ?
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className={`alert mt-3 alert-warning`}
                >
                  Authentication in Progress, please wait!
                </motion.div> :
                <>
                  <p className="mb-3 text-muted">Use your organization (Microsoft/AD) account to access this application.</p>
                  <button className="btn btn-primary btn-lg w-100 shadow-sm" onClick={handleADLogin}>
                    <span className='d-flex flex-row align-items-center gap-2 w-100 justify-content-center'>
                      Sign in <TiVendorMicrosoft size={18} />
                    </span>
                  </button>
                </>
            }
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LoginForm;